import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import Header from "@/components/Header";
import MatchScoreRing from "@/components/MatchScoreRing";
import AdminQuickActions from "@/components/AdminQuickActions";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Sparkles, MessageSquare, Calendar, Award, MapPin, ChevronRight, GitBranch } from "lucide-react";
import CoachingTreePanel from "@/components/CoachingTreePanel";
import StatusBadges from "@/components/StatusBadges";

const _PROVENANCE_STYLE = {
  admin_verified:    { label: "Admin verified",    cls: "bg-blaze text-white",     dot: "🛡" },
  employer_verified: { label: "Employer verified", cls: "bg-blaze text-white",     dot: "✓" },
  scraped:           { label: "School-site source", cls: "bg-volt/20 text-volt border border-volt/40", dot: "◉" },
  mentor_endorsed:   { label: "Peer endorsed",     cls: "bg-volt/20 text-volt border border-volt/40",  dot: "★" },
  self_reported:     { label: "Self reported",     cls: "bg-neutral-800 text-neutral-400",             dot: "◇" },
};

function ProvenanceChip({ provenance, source }) {
  const cfg = _PROVENANCE_STYLE[provenance] || _PROVENANCE_STYLE.self_reported;
  return (
    <span
      data-testid={`provenance-${provenance}`}
      title={source ? `${cfg.label} · ${source}` : cfg.label}
      className={`text-[9px] tracking-widest px-1.5 py-0.5 heading ${cfg.cls}`}
    >
      {cfg.dot} {cfg.label.toUpperCase()}
    </span>
  );
}

export default function CandidateDetail() {
  const { coachId } = useParams();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [coach, setCoach] = useState(null);
  const [vetting, setVetting] = useState(null);
  const [loadingVet, setLoadingVet] = useState(false);
  // Target hire level the AD is sizing this coach against. `null` means use
  // the universal CIR; HS/D3/D2/D1 re-scale the score for that target.
  // We auto-detect from the viewer's program_segment/division on mount so
  // an HS AD lands on the HS score by default — way more conversion-friendly.
  const [targetLevel, setTargetLevel] = useState(null);

  useEffect(() => {
    api.get(`/coaches/${coachId}`).then(({ data }) => setCoach(data));
  }, [coachId]);

  useEffect(() => {
    // Auto-detect target level from AD's own program. Only fires once.
    if (user?.role !== "athletic_director") return;
    api.get("/program/me").then(({ data }) => {
      const seg = (data?.program_segment || "").toLowerCase();
      const div = (data?.program_division || "").toUpperCase();
      let detected = null;
      if (seg === "hs_ad" || seg === "hs_head_coach") detected = "HS";
      else if ((seg === "college_varsity" || seg === "college_head_coach") && ["D1", "D2", "D3"].includes(div)) detected = div;
      else if (seg === "college_varsity" || seg === "college_head_coach") detected = "D1";
      if (detected) setTargetLevel(detected);
    }).catch(() => {});
  }, [user]);

  const runVet = async () => {
    setLoadingVet(true);
    try {
      const { data } = await api.get(`/vetting/${coachId}`);
      setVetting(data);
      toast.success("Vetting report generated.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Vetting failed");
    } finally { setLoadingVet(false); }
  };

  const startMessage = async () => {
    if (!coach.user_id) return toast.info("This coach is scouted but unclaimed. Invitation flow coming soon.");
    try {
      const { data } = await api.post("/messages", {
        to_user_id: coach.user_id,
        body: `Hi ${coach.name.split(" ")[0]}, we're considering you for an opening. Open to chat?`,
      });
      window.location.href = `/messages/${data.thread_key}`;
    } catch (e) {
      toast.error("Could not send message");
    }
  };

  if (!coach) return <Shell><div className="p-10 heading text-3xl">LOADING…</div></Shell>;

  const baseCir = coach.qbr || { score: 0, components: [], record: "0-0" };
  const cirByTarget = coach.cir_by_target || null;
  // Pick the display CIR: targetLevel-aware when the AD has flipped the
  // toggle, otherwise the universal CIR. The toggle is the differentiator
  // the user asked for — same coach, different lens.
  const qbr =
    targetLevel && cirByTarget && cirByTarget[targetLevel]
      ? { ...baseCir, ...cirByTarget[targetLevel], record: baseCir.record, bullets: baseCir.bullets }
      : baseCir;
  const approvedEnrich = (coach.enrichment_suggestions || []).filter((s) => s.status === "approved");

  return (
    <Shell>
      <div className="px-4 md:px-8 lg:px-12 py-8" data-testid="candidate-detail-page">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8">
            <div className="text-xs tracking-widest text-volt">{coach.gender?.toUpperCase()} · {coach.sport?.toUpperCase()} · {coach.primary_level?.toUpperCase()}</div>
            <h1 className="heading text-4xl md:text-5xl lg:text-6xl mt-2">{(coach.name || "Unnamed Coach").toUpperCase()}</h1>
            {!coach.claimed && <div className="text-blaze text-xs tracking-widest mt-2">SCOUTED BY HEADHUNTERAGENT · UNCLAIMED PROFILE</div>}
            <StatusBadges
              className="mt-2"
              coachPro={coach.status_badges?.coach_pro}
              foundingMember={coach.status_badges?.founding_member}
            />

            <div className="mt-3">
              <AdminQuickActions
                entity="coach"
                id={coach.id}
                name={coach.name}
                fields={[
                  { key: "name", label: "Name", value: coach.name },
                  { key: "headline", label: "Headline", value: coach.headline, multiline: true },
                  { key: "current_organization", label: "Organization", value: coach.current_organization },
                  { key: "current_role", label: "Current Role", value: coach.current_role },
                  { key: "current_division", label: "Current Division", value: coach.current_division },
                  { key: "email", label: "Email", value: coach.email },
                  { key: "phone", label: "Phone", value: coach.phone },
                ]}
                onSaved={(updated) => setCoach({ ...coach, ...updated })}
              />
            </div>
            {coach.headline && <div className="text-neutral-300 mt-3 max-w-2xl">{coach.headline}</div>}
            {coach.lineage?.length > 1 && (
              <div className="mt-4 border border-neutral-800 bg-neutral-950/60 px-4 py-3 inline-flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="coaching-lineage">
                <span className="text-[10px] tracking-widest text-volt flex items-center gap-1.5 mr-1"><GitBranch size={12} /> COACHING LINEAGE</span>
                {coach.lineage.map((n, i) => (
                  <span key={i} className="flex items-center gap-2">
                    {i > 0 && <ChevronRight size={13} className="text-neutral-600" />}
                    {n.generation !== "coach" && n.coach_profile_id ? (
                      <Link
                        to={`/coaches/${n.coach_profile_id}`}
                        data-testid={`lineage-node-${n.generation}`}
                        className="group flex flex-col leading-tight hover:text-volt transition-colors"
                      >
                        <span className="text-sm text-neutral-100 group-hover:text-volt">
                          {n.name}{n.hof && <span className="ml-1 text-[9px] text-amber-400 tracking-widest align-top">HOF</span>}
                        </span>
                        <span className="text-[10px] text-neutral-500">{[n.role, n.organization].filter(Boolean).join(" · ")}</span>
                      </Link>
                    ) : (
                      <span data-testid={`lineage-node-${n.generation}`} className="flex flex-col leading-tight">
                        <span className={`text-sm ${n.generation === "coach" ? "text-volt" : "text-neutral-100"}`}>
                          {n.name}{n.hof && <span className="ml-1 text-[9px] text-amber-400 tracking-widest align-top">HOF</span>}
                        </span>
                        <span className="text-[10px] text-neutral-500">{[n.role, n.organization].filter(Boolean).join(" · ")}</span>
                      </span>
                    )}
                  </span>
                ))}
              </div>
            )}
            <div className="mt-4 flex flex-wrap gap-3 text-sm">
              {coach.location && <Meta icon={MapPin}>{coach.location}</Meta>}
              {coach.affiliations?.length > 0 && <Meta icon={Award}>{coach.affiliations.join(" · ")}</Meta>}
            </div>
            {user?.role === "athletic_director" && (
              <div className="mt-6 flex flex-wrap gap-3">
                <button onClick={runVet} disabled={loadingVet} data-testid="run-vet-btn" className="bg-volt text-white heading text-base px-5 py-3 hover:glow-volt transition-all disabled:opacity-50 flex items-center gap-2">
                  <Sparkles size={16} /> {loadingVet ? "GENERATING…" : "VETTING REPORT"}
                </button>
                <Link to={`/contracts/${coachId}${params.get("job") ? `?job=${params.get("job")}` : ""}`} data-testid="draft-contract-btn" className="border border-blaze text-blaze heading text-base px-5 py-3 hover:bg-blaze hover:text-white transition-all flex items-center gap-2">
                  <Sparkles size={16} /> DRAFT CONTRACT
                </Link>
                {coach.user_id && (
                  <button onClick={startMessage} data-testid="message-coach-btn" className="border border-volt text-volt heading text-base px-5 py-3 hover:bg-volt hover:text-white transition-all flex items-center gap-2">
                    <MessageSquare size={16} /> MESSAGE COACH
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="lg:col-span-4 border border-neutral-800 bg-neutral-950 p-6">
            {/* Target-level toggle — re-scores the CIR against the level the AD is hiring for. */}
            {cirByTarget && user?.role === "athletic_director" && (
              <div className="mb-4" data-testid="cir-target-toggle">
                <div className="text-[10px] tracking-widest text-neutral-500 mb-2">SCORE THIS COACH AGAINST →</div>
                <div className="flex flex-wrap gap-1">
                  <button
                    onClick={() => setTargetLevel(null)}
                    data-testid="cir-target-base"
                    className={`text-[10px] tracking-widest px-2 py-1 border transition-colors ${
                      targetLevel === null ? "border-volt text-volt bg-volt/10" : "border-neutral-700 text-neutral-500 hover:text-neutral-200"
                    }`}
                  >BASE</button>
                  {["HS", "D3", "D2", "D1"].map((lvl) => {
                    const r = cirByTarget[lvl];
                    return (
                      <button
                        key={lvl}
                        onClick={() => setTargetLevel(lvl)}
                        data-testid={`cir-target-${lvl}`}
                        className={`text-[10px] tracking-widest px-2 py-1 border transition-colors ${
                          targetLevel === lvl ? "border-volt text-volt bg-volt/10" : "border-neutral-700 text-neutral-500 hover:text-neutral-200"
                        }`}
                      >
                        {lvl} · {r?.score ?? "–"}
                      </button>
                    );
                  })}
                </div>
                {targetLevel && (
                  <div className="text-[10px] text-neutral-500 mt-2 leading-snug">
                    Showing how this coach scores if hired into a <span className="text-volt">{targetLevel}</span> program.
                  </div>
                )}
              </div>
            )}
            <div className="flex items-center gap-4">
              <MatchScoreRing score={qbr.score} label="CIR" size={130} stroke={12} />
              <div className="min-w-0">
                <div className="heading text-2xl">COACH CIR</div>
                {qbr.tier && (
                  <div className="inline-block mt-1 px-2 py-0.5 bg-blaze text-white text-[10px] tracking-widest" data-testid="candidate-qbr-tier">
                    {String(qbr.tier).toUpperCase()}
                  </div>
                )}
                {targetLevel && (
                  <div className="text-[10px] text-volt mt-1 tracking-widest" data-testid="candidate-qbr-target-tag">
                    {targetLevel} HIRE FIT
                  </div>
                )}
                <div className="text-mono text-xs text-neutral-500 mt-2">Record: {qbr.record}</div>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              {qbr.components.map((c) => (
                <div key={c.label}>
                  <div className="flex items-center justify-between text-xs text-neutral-400">
                    <span className="tracking-widest">{(c.label || "").toUpperCase()}</span>
                    <span className="text-mono">{c.value}/{c.max}</span>
                  </div>
                  <div className="h-1 bg-neutral-800 mt-1">
                    <div className="h-1 bg-blaze" style={{ width: `${(c.value / c.max) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
            {(qbr.bullets || []).length > 0 && (
              <div className="mt-5 border-t border-neutral-800 pt-4">
                <div className="text-volt text-[10px] tracking-widest mb-2">CIR HIGHLIGHTS</div>
                <ul className="space-y-1 text-xs text-neutral-300" data-testid="candidate-qbr-bullets">
                  {qbr.bullets.slice(0, 6).map((b, i) => (
                    <li key={i} className="flex items-start gap-2"><span className="text-blaze mt-0.5">▸</span><span>{b}</span></li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Vetting */}
        {vetting && (
          <div className="mt-8 border border-volt p-6 bg-[#0A0F18]" data-testid="vetting-report">
            <div className="text-xs tracking-widest text-volt">VETTINGAGENT REPORT · {new Date(vetting.generated_at).toLocaleString()}</div>
            <div className="heading text-2xl mt-2">DUE DILIGENCE</div>
            <p className="text-neutral-200 mt-3">{vetting.summary}</p>
            {vetting.discrepancies?.length > 0 && (
              <div className="mt-4 border-l-2 border-blaze pl-3">
                <div className="text-blaze text-[10px] tracking-widest">DATA DISCREPANCIES FLAGGED</div>
                <ul className="text-neutral-300 text-sm mt-1 list-disc pl-5">
                  {vetting.discrepancies.map((d, i) => <li key={i}>{d}</li>)}
                </ul>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {vetting.performance_trends?.length > 0 && (
                <div>
                  <div className="text-volt text-[10px] tracking-widest mb-2">PERFORMANCE TRENDS</div>
                  <ul className="space-y-2 text-sm">
                    {vetting.performance_trends.map((t, i) => (
                      <li key={i} className="border-l-2 border-neutral-700 pl-3">
                        <div className="heading text-base">{t.label}</div>
                        <div className="text-neutral-400 text-xs">{t.detail}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {vetting.interview_questions?.length > 0 && (
                <div>
                  <div className="text-volt text-[10px] tracking-widest mb-2">SUGGESTED INTERVIEW QUESTIONS</div>
                  <ul className="space-y-2 text-sm list-decimal pl-5 text-neutral-300">
                    {vetting.interview_questions.map((q, i) => <li key={i}>{q}</li>)}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Career timeline (TKG) */}
        <div className="mt-10">
          <div className="text-xs tracking-widest text-blaze">TEMPORAL KNOWLEDGE GRAPH</div>
          <h2 className="heading text-3xl mt-2">CAREER TIMELINE</h2>
          <div className="mt-6 relative pl-8 space-y-6 border-l-2 border-neutral-800">
            {(coach.coaching_history || []).slice().sort((a, b) => (b.start_year || 0) - (a.start_year || 0)).map((s, i) => (
              <div key={i} className="relative" data-testid={`tkg-stint-${i}`}>
                <span className="absolute -left-[37px] top-1.5 w-4 h-4 bg-blaze ring-4 ring-[#0A0A0A]" />
                <div className="text-mono text-xs text-volt flex items-center gap-2 flex-wrap">
                  <span>{s.start_year} — {s.end_year || "PRESENT"} · {s.division || s.level}</span>
                  {s.provenance && <ProvenanceChip provenance={s.provenance} source={s.provenance_source} />}
                </div>
                <div className="heading text-2xl mt-1">{s.role} · {s.organization}</div>
                {(s.wins != null && s.losses != null) && <div className="text-mono text-sm text-neutral-400 mt-1">Record: {s.wins}-{s.losses}{s.conference ? ` · ${s.conference}` : ""}</div>}
                {s.championships?.length > 0 && (
                  <div className="text-sm text-neutral-300 mt-1">🏆 {s.championships.join(" · ")}</div>
                )}
                {s.accolades?.length > 0 && (
                  <div className="text-sm text-neutral-400 mt-1">{s.accolades.join(" · ")}</div>
                )}
                {s.notes && <div className="text-sm text-neutral-500 mt-1 italic">{s.notes}</div>}
              </div>
            ))}
          </div>
        </div>

        {/* Philosophy / Education */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border border-neutral-800 p-6 bg-appcard">
            <div className="text-xs tracking-widest text-volt">PHILOSOPHY</div>
            <p className="text-neutral-300 mt-3 leading-relaxed">{coach.philosophy || "—"}</p>
          </div>
          <div className="border border-neutral-800 p-6 bg-appcard">
            <div className="text-xs tracking-widest text-volt">PLAYING & EDUCATION</div>
            <div className="text-neutral-300 mt-3 space-y-2 text-sm">
              <div><span className="text-neutral-500 text-xs tracking-widest mr-2">HS</span> {coach.playing_history?.hs_team || "—"}{coach.playing_history?.hs_captain ? " (Captain)" : ""}</div>
              <div><span className="text-neutral-500 text-xs tracking-widest mr-2">COLLEGE</span> {coach.playing_history?.college_team || "—"}{coach.playing_history?.college_division ? ` (${coach.playing_history.college_division})` : ""}{coach.playing_history?.college_captain ? " · Captain" : ""}</div>
              {coach.playing_history?.accolades?.length > 0 && <div className="text-neutral-400 text-xs">{coach.playing_history.accolades.join(" · ")}</div>}
              {coach.playing_history?.professional && <div className="text-blaze text-xs tracking-widest">PRO: {coach.playing_history.professional_team}</div>}
              <div className="pt-2 border-t border-neutral-800"><span className="text-neutral-500 text-xs tracking-widest mr-2">DEGREE</span> {coach.education?.degree || "—"} · {coach.education?.school || "—"}{coach.education?.gpa ? ` · GPA ${coach.education.gpa}` : ""}</div>
              {coach.education?.masters && <div><span className="text-neutral-500 text-xs tracking-widest mr-2">MASTERS</span> {coach.education?.masters_school || "Yes"}</div>}
            </div>
          </div>
        </div>

        {/* Approved enrichments */}
        {approvedEnrich.length > 0 && (
          <div className="mt-8 border border-neutral-800 p-6 bg-appcard">
            <div className="text-xs tracking-widest text-volt">APPROVED PUBLIC SIGNALS · COACHPROFILEAGENT</div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              {approvedEnrich.map((s) => (
                <div key={s.id} className="border-l-2 border-volt pl-3">
                  <div className="heading text-base">{s.title}</div>
                  <div className="text-neutral-400 text-xs mt-1">{s.description}</div>
                  <div className="text-mono text-[10px] text-neutral-500 mt-1">{s.source} · {s.source_date?.slice(0, 10)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Coaching Tree — inferred mentors + disciples from stint overlap */}
        <div className="mt-8">
          <CoachingTreePanel
            mentorsInferred={coach.mentors_inferred || []}
            disciples={coach.disciples || []}
            coachId={coach.id}
            coachName={coach.display_name || coach.name}
          />
        </div>

        {/* Mentor tree */}
        {((coach.mentors || []).length > 0 || (coach.coaching_history || []).some((s) => (s.mentored_under || []).length > 0)) && (
          <div className="mt-8 border border-neutral-800 p-6 bg-appcard" data-testid="mentor-tree">
            <div className="text-xs tracking-widest text-volt">MENTOR TREE</div>
            <div className="heading text-2xl mt-1">COACHED UNDER</div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              {(() => {
                const seen = new Set();
                return [
                  // Stint-level mentors first — they carry the richest context (org + years)
                  ...((coach.coaching_history || []).flatMap((s) =>
                    (s.mentored_under || []).map((m) => ({ ...m, _ctx: `${s.organization} · ${s.start_year}-${s.end_year || "present"}` }))
                  )),
                  // Career mentors — skip inferred ones (already shown in the Coaching Tree panel above)
                  ...((coach.mentors || [])
                    .filter((m) => m.inferred_from !== "stint_overlap")
                    .map((m) => ({ ...m, _ctx: m.relationship || "Career mentor" }))),
                ].filter((m) => {
                  const k = (m.name || "").toLowerCase().trim();
                  if (!k || seen.has(k)) return false;
                  seen.add(k);
                  return true;
                });
              })().map((m, i) => (
                <div key={i} className={`p-3 border-l-2 ${m.hof ? "border-blaze" : "border-neutral-700"}`}>
                  <div className="flex items-center justify-between">
                    <div className="heading text-base">{m.name}</div>
                    {m.hof && <span className="text-blaze text-[10px] tracking-widest border border-blaze px-1.5 py-0.5">HOF</span>}
                  </div>
                  <div className="text-neutral-400 text-xs">{m.role}{m.organization ? ` · ${m.organization}` : ""}</div>
                  <div className="text-neutral-600 text-[10px] tracking-widest mt-1">{m._ctx}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* MaxPreps + external lookup */}
        <div className="mt-6 flex flex-wrap gap-3 items-center text-sm" data-testid="external-lookups">
          <a
            href={`https://www.maxpreps.com/search/default.aspx?search=${encodeURIComponent(coach.name)}`}
            target="_blank" rel="noopener noreferrer"
            data-testid="maxpreps-link"
            className="border border-neutral-700 px-3 py-1.5 hover:border-volt text-neutral-300 hover:text-volt transition-colors uppercase tracking-widest text-xs"
          >
            Lookup on MaxPreps ↗
          </a>
          {coach.affiliations?.includes("USILA") && (
            <a
              href={`https://www.usila.org/sports/lax/index?path=mlax/search?searchstring=${encodeURIComponent(coach.name)}`}
              target="_blank" rel="noopener noreferrer"
              className="border border-neutral-700 px-3 py-1.5 hover:border-volt text-neutral-300 hover:text-volt transition-colors uppercase tracking-widest text-xs"
            >
              USILA ↗
            </a>
          )}
        </div>
      </div>
    </Shell>
  );
}

function Meta({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-1.5 text-neutral-400 border border-neutral-800 px-3 py-1.5">
      <Icon size={14} /> <span>{children}</span>
    </div>
  );
}
function Shell({ children }) { return <div className="min-h-screen bg-appbg text-white"><Header />{children}</div>; }
